import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";
import { upsertFetcherConfig } from "../../../../features/fetcherSettingsSlice";
import { setDisplayErrorSnackBar } from "../../../../features/userSlice";
import { nextLocalId } from "../helpers/localIds";
import { nowStamp } from "../helpers/formatters";
import ConfigDefinitionFields, { ConfigDraft } from "./ConfigDefinitionFields";
import CreateReportModal from "../Reports/CreateReportModal";

interface Props {
  open: boolean;
  onClose: () => void;
  /** pre-fills the form - used by Duplicate, which copies the whole recipe */
  initial?: ConfigDraft | null;
}

// 'none' sub-filtering and 'csv' format are what most configs use
const EMPTY_DRAFT: ConfigDraft = {
  name: "",
  report: "",
  cm: "",
  sfm: "2",
  ft: "1",
};

const CreateConfigModal = ({ open, onClose, initial }: Props) => {
  const dispatch = useDispatch();
  const { configs, reports, formatTypes } = useSelector(
    (state: AppState) => state.fetcherSettings,
  );

  const [draft, setDraft] = useState<ConfigDraft>(EMPTY_DRAFT);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(initial ?? EMPTY_DRAFT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // an exact recipe copy on the same report is refused on every path
  const duplicates = draft.report
    ? configs.filter(
        (row) =>
          row.report === draft.report &&
          row.cm === draft.cm &&
          row.sfm === draft.sfm &&
          row.ft === draft.ft,
      )
    : [];

  const isValid =
    !!draft.name.trim() &&
    !!draft.report &&
    !!draft.cm &&
    !!draft.sfm &&
    !!draft.ft &&
    duplicates.length === 0;

  const handleCreate = () => {
    // TODO: POST /aladdin_api/v1/data_collection/fetcher_config/ - returns
    // id + shared_uid
    const report = reports.find((row) => row.id === draft.report);
    const id = nextLocalId(configs);
    dispatch(
      upsertFetcherConfig({
        id,
        name: draft.name.trim(),
        report: draft.report,
        report_name: report?.name ?? "",
        iface: report?.iface ?? "",
        cm: draft.cm,
        sfm: draft.sfm,
        ft: draft.ft,
        ftn: formatTypes.find((row) => row.id === draft.ft)?.name ?? "",
        ctype: report?.ctype ?? "",
        used: 0,
        twin: [],
        brands: [],
        mod: nowStamp(),
      }),
    );
    dispatch(
      setDisplayErrorSnackBar({
        message: `${draft.name.trim()} (${id}) created.`,
        type: "success",
      }),
    );
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: "760px", maxWidth: "94vw" } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>New Config</DialogTitle>
        <Divider />
        <DialogContent sx={{ px: 3, py: 2 }}>
          <ConfigDefinitionFields
            draft={draft}
            onChange={setDraft}
            canPickReport
            onCreateReport={() => setIsReportDialogOpen(true)}
          />
          {duplicates.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <b>Blocked - 100% duplicate.</b>{" "}
              {duplicates
                .map((row) => `${row.name} (${row.id})`)
                .join(", ")}{" "}
              already has this exact recipe.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Box sx={{ flex: 1 }} />
          <Button color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={!isValid}
            onClick={handleCreate}
          >
            Create config
          </Button>
        </DialogActions>
      </Dialog>

      <CreateReportModal
        open={isReportDialogOpen}
        onClose={() => setIsReportDialogOpen(false)}
        onCreated={(reportId) => {
          // the new report comes back pre-selected on the config underneath
          setDraft((previous) => ({ ...previous, report: reportId }));
          setIsReportDialogOpen(false);
        }}
      />
    </>
  );
};

export default CreateConfigModal;
